/**
 * Story 182 (issue #3015) — the client's view of the periodic return rule of an external job.
 *
 * The shape is the frozen cross-repo contract (03-dominio.md §Contrato cross-repo congelado):
 *   { form: 'RELATIVE' | 'SERIES', weekdays: ['MO'..'SU'], weekInterval: N, time: 'HH:mm', seriesStart: 'yyyy-MM-dd' | null }
 *
 * `time` and `seriesStart` travel as LOCAL STRINGS, never as instants: a JS `Date` serialised to ISO moves a
 * day in zones west of UTC, and `new Date('2026-10-05')` is midnight UTC. Every helper here therefore reads
 * and writes the digits of those strings and builds a `Date` only from its numeric parts.
 *
 * Kept free of React so the editor, the client validation (`utils.js`) and the summary share ONE definition
 * of "complete".
 */

export const PERIODIC_FORM = { RELATIVE: 'RELATIVE', SERIES: 'SERIES' }

/** iCalendar day codes in week order (the same `key`s as CalendarRepeatComponent). */
export const WEEKDAY_CODES = ['MO', 'TU', 'WE', 'TH', 'FR', 'SA', 'SU']

/** Week order (Monday first), without duplicates; unknown codes are dropped. */
export const sortWeekdays = (weekdays) => WEEKDAY_CODES.filter((code) => (weekdays || []).includes(code))

const isKnownForm = (form) => form === PERIODIC_FORM.RELATIVE || form === PERIODIC_FORM.SERIES

export const isValidWeekInterval = (weekInterval) => Number.isInteger(weekInterval) && weekInterval >= 1

/**
 * Complete <=> form known AND weekdays non-empty (RELATIVE: exactly one) AND interval >= 1 AND time informed
 * AND (SERIES => start date informed). The client only rules on this for NEW jobs (RN2.a of #80); a form the
 * client does not know is "not complete" here, but the save check leaves that case to the server.
 */
export const isPeriodicRuleComplete = (rule) => {
  if (!rule || !isKnownForm(rule.form)) {
    return false
  }
  const weekdays = Array.isArray(rule.weekdays) ? rule.weekdays : []
  if (rule.form === PERIODIC_FORM.RELATIVE && weekdays.length !== 1) {
    return false
  }
  if (rule.form === PERIODIC_FORM.SERIES && weekdays.length < 1) {
    return false
  }
  if (!isValidWeekInterval(rule.weekInterval)) {
    return false
  }
  if (!rule.time) {
    return false
  }
  return rule.form !== PERIODIC_FORM.SERIES || Boolean(rule.seriesStart)
}

/** A rule of the given form with nothing filled in yet. */
/**
 * The return time a new rule starts with. The time is mandatory (RN6, E11) and a rule without it cannot be
 * resolved to an instant, so instead of an empty required field the editor offers a sensible default that the
 * user only changes when the supplier returns at another hour (decision of 2026-10-05, client side only: Sun
 * still rejects a rule without time).
 */
export const DEFAULT_RETURN_TIME = '08:00'

export const createEmptyPeriodicRule = (form) => ({
  form, weekdays: [], weekInterval: null, time: DEFAULT_RETURN_TIME, seriesStart: null
})

/**
 * Changing the form (RN29.1). RELATIVE -> SERIES keeps nothing (the series is defined anew, back to the
 * default time). SERIES ->
 * RELATIVE keeps the interval and the time, takes as the single day the FIRST selected one in week order
 * (none selected => none) and drops the start date.
 */
export const changePeriodicForm = (rule, form) => {
  if (form === PERIODIC_FORM.RELATIVE && rule?.form === PERIODIC_FORM.SERIES) {
    return {
      form,
      weekdays: sortWeekdays(rule.weekdays).slice(0, 1),
      weekInterval: rule.weekInterval ?? null,
      time: rule.time ?? DEFAULT_RETURN_TIME,
      seriesStart: null
    }
  }
  return createEmptyPeriodicRule(form)
}

const LOCAL_DATE = /^([0-9]{4})-([0-9]{2})-([0-9]{2})$/
const LOCAL_TIME = /^([0-9]{2}):([0-9]{2})$/

/** 'yyyy-MM-dd' -> local Date built from its own digits; null when it is not a real calendar date. */
export const parseLocalDate = (text) => {
  const match = LOCAL_DATE.exec(text || '')
  if (!match) {
    return null
  }
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const date = new Date(year, month - 1, day)
  const real = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
  return real ? date : null
}

const pad2 = (value) => String(value).padStart(2, '0')

/** Local Date -> 'yyyy-MM-dd' from its LOCAL parts (no toISOString); null for a missing or invalid date. */
export const formatLocalDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null
  }
  return date.getFullYear() + '-' + pad2(date.getMonth() + 1) + '-' + pad2(date.getDate())
}

/** 'HH:mm' -> a Date on an arbitrary day carrying that wall-clock time; null when it is not a valid time. */
export const parseLocalTime = (text) => {
  const match = LOCAL_TIME.exec(text || '')
  if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) {
    return null
  }
  return new Date(1970, 0, 1, Number(match[1]), Number(match[2]))
}

/** Local Date -> 'HH:mm' from its local parts; null for a missing or invalid time. */
export const formatLocalTime = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null
  }
  return pad2(date.getHours()) + ':' + pad2(date.getMinutes())
}
